import { Box, Text, useInput } from 'ink';
import React, { useMemo, useState, useEffect } from 'react';

// * utils
import { flattenTree, stringifyTree } from '../utils/index.js';

// * components
import Hint from './Hint.js';

// * types
import type { MultiVirtualTreeSelectProps } from '../types/index.js';

export const MultiVirtualTreeSelect = ({
  tree,
  onChange,
  onSelect,
  options = {},
}: MultiVirtualTreeSelectProps) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [selected, setSelected] = useState<boolean>(false);
  const { previewColor, indicatorColor } = options;

  const flattenedTree = useMemo(() => flattenTree(tree).slice(1), [tree]);
  const selectedPaths = selectedIndices.map(i => flattenedTree[i]!);
  const activePath = flattenedTree[activeIndex]!;
  const maxIndex = flattenedTree.length - 1;

  useEffect(() => {
    onChange?.(activePath, selectedPaths);
  }, [activePath]);

  useInput(
    (input, key) => {
      if (key.return) {
        setSelected(true);
        onSelect?.(selectedPaths);
        return;
      }

      const isSpace = input === ' ';

      if (isSpace) {
        return setSelectedIndices(prev => {
          const newSelectedIndices = [...prev];
          const index = newSelectedIndices.indexOf(activeIndex);

          if (index === -1) newSelectedIndices.push(activeIndex);
          else newSelectedIndices.splice(index, 1);

          return newSelectedIndices;
        });
      }

      if (key.upArrow) return setActiveIndex(prev => Math.max(0, --prev));
      if (key.downArrow) return setActiveIndex(prev => Math.min(++prev, maxIndex));
    },
    { isActive: !selected }
  );

  return (
    <Box flexDirection='column' rowGap={1}>
      <Hint />
      <Text>
        {stringifyTree(tree, activePath, selectedPaths, { multi: true, color: indicatorColor })}
      </Text>
      <Text color={previewColor ?? 'blue'}>{activePath}</Text>
    </Box>
  );
};
